import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, FolderPlus, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { MergePicker, PossibleDuplicates } from "@/components/crm/CustomerMerge";
import { CustomerEditForm } from "@/components/crm/CustomerEditForm";
import { CustomerActions, CustomerHistory } from "@/components/crm/CustomerActions";
import { StageBadge, btnGold, btnLine, inp, usePeople } from "@/lib/crm";
import { money } from "@/lib/pricing";

function Reassign({ c, people, onDone }) {
  const [rep, setRep] = useState(c.rep_id);
  if (people.me.role === "rep") return null;
  const save = async () => {
    try {
      onDone((await api.post(`/customers/${c.id}/assign`, { rep_id: rep })).data);
      toast.success("Customer reassigned");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="customer-reassign">
      <select data-testid="customer-reassign-select" value={rep} onChange={(e) => setRep(e.target.value)} className={`${inp} w-56`}>
        {people.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <button data-testid="customer-reassign-button" disabled={rep === c.rep_id} onClick={save} className={btnLine}>Reassign</button>
    </div>
  );
}

function PresetField({ c, onDone }) {
  const [list, setList] = useState([]);
  useEffect(() => { if (c.can_set_preset) api.get("/markup-presets").then((r) => setList(r.data)).catch(() => {}); }, [c.can_set_preset]);
  if (!c.can_set_preset) return <p data-testid="customer-markup-preset" className="text-xs text-slate-400">Markup preset: <span className="font-semibold text-slate-200">{c.markup_preset_name}</span></p>;
  const save = async (pid) => {
    try {
      const keep = ["name", "company", "email", "phone", "address", "notes"].reduce((a, k) => ({ ...a, [k]: c[k] || "" }), {});
      onDone((await api.put(`/customers/${c.id}`, { ...keep, markup_preset_id: pid })).data);
      toast.success("Markup preset saved. New estimates use it");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <label className="flex items-center gap-2 text-xs text-slate-400">Markup preset
      <span className="w-48"><select data-testid="customer-markup-preset-select" value={c.markup_preset_id || ""} onChange={(e) => save(e.target.value)} className={inp}>
        {list.map((p) => <option key={p.id} value={p.id}>{p.name}{p.multiplier ? ` (×${p.multiplier})` : ""}</option>)}
      </select></span>
    </label>
  );
}

function NewProject({ cid, onDone }) {
  const [f, setF] = useState({ name: "", site_address: "" });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Project name is required");
    try {
      onDone((await api.post("/projects", { ...f, customer_id: cid })).data);
      setF({ name: "", site_address: "" });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="new-project-form">
      <input data-testid="project-name-input" placeholder="Project name, e.g. Lake House" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} w-64`} />
      <input data-testid="project-site-input" placeholder="Site address" value={f.site_address} onChange={(e) => setF({ ...f, site_address: e.target.value })} className={`${inp} w-72`} />
      <button data-testid="project-create-button" onClick={save} className={btnGold}><FolderPlus className="h-4 w-4" /> Add project</button>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const people = usePeople();
  const [c, setC] = useState(null);
  const [picking, setPicking] = useState(false);
  const editing = sp.get("edit") === "1";
  const setEditing = (on) => setSp(on ? { edit: "1" } : {}, { replace: true });
  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setC(r.data)).catch((e) => { toast.error(errorMessage(e)); nav("/customers"); }), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <div className="h-40 animate-pulse rounded-2xl bg-slate-900/30" />;
  if (editing) return <CustomerEditForm c={c} people={people} onCancel={() => setEditing(false)} onSaved={(d) => { setC(d); setEditing(false); }} />;
  return (
    <div className="space-y-8" data-testid="customer-detail-page">
      <div className="rise">
        <div className="flex items-center justify-between">
          <Link to="/customers" data-testid="customer-back-link" className="inline-flex min-h-[44px] items-center gap-1 text-[17px] text-[#0062CC]"><ChevronLeft className="h-5 w-5" /> Customers</Link>
          <button data-testid="customer-edit-button" onClick={() => setEditing(true)} className="min-h-[44px] px-2 text-[17px] text-[#0062CC]">Edit</button>
        </div>
        <h1 data-testid="customer-detail-name" className="mt-2 font-display text-4xl font-semibold text-slate-50 sm:text-5xl">{c.name}</h1>
        {c.archived && <p data-testid="customer-archived-badge" className="mt-2 inline-flex rounded-full bg-black/[0.06] px-3 py-1 text-[13px] font-semibold text-[#3C3C43]">Archived</p>}
        <p className="mt-2 text-sm text-slate-400">{[c.company, c.email, c.phone, c.address].filter(Boolean).join(" · ")}</p>
        <p className="mt-1 text-xs text-slate-500" data-testid="customer-detail-assignment">{c.showroom_name} · Sales rep: {c.rep_name}</p>
      </div>
      <Reassign c={c} people={people} onDone={setC} />
      <PresetField c={c} onDone={setC} />
      <section className="space-y-4 rounded-2xl border border-slate-800 p-6" data-testid="customer-projects">
        <h2 className="font-display text-2xl text-slate-50">Projects</h2>
        <NewProject cid={c.id} onDone={(p) => nav(`/projects/${p.id}`)} />
        <div className="divide-y divide-slate-800/60">
          {c.projects.map((p, i) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`customer-project-${i}`} className="flex items-center justify-between gap-4 py-3 transition-colors hover:bg-amber-500/5">
              <div><p className="font-semibold text-slate-100">{p.name}</p><p className="text-xs text-slate-500">{p.site_address || "No site address"} · {p.estimate_count} estimate{p.estimate_count === 1 ? "" : "s"}</p></div>
              <div className="flex items-center gap-3">{p.status ? <StageBadge stage={p.status} /> : <span className="text-xs text-slate-500">No estimate</span>}<span className="font-mono-num text-sm text-slate-200">{money(p.total)}</span></div>
            </Link>
          ))}
          {c.projects.length === 0 && <p className="py-6 text-sm text-slate-500">No projects yet.</p>}
        </div>
      </section>
      {c.notes && <p data-testid="customer-notes" className="whitespace-pre-wrap rounded-[12px] bg-white px-4 py-3 text-[15px] text-[#3C3C43]">{c.notes}</p>}
      <CustomerHistory items={c.timeline} />
      <PossibleDuplicates c={c} />
      {c.can_delete && <button data-testid="customer-merge-button" onClick={() => setPicking(true)} className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#007AFF]/10 px-4 text-[15px] font-medium text-[#0062CC] hover:bg-[#007AFF]/15"><GitMerge className="h-4 w-4" /> Merge with…</button>}
      {picking && <MergePicker c={c} onClose={() => setPicking(false)} />}
      <CustomerActions c={c} onChanged={load} onDeleted={() => nav("/customers")} />
    </div>
  );
}
